import { getFilesIds } from '@components/pages/Products/components/RouteProduct/useGetFilesFromRoute';
import useBaseDataContext from '@context/useBaseDataContext';
import { CustomsPaymentsMode, getLocalizationById, parseUrl } from '@context/useMyRouting/helpers';
import { LinearProgress } from '@material-ui/core';
import Button from '@mui/material/Button';
import useBasket from '@pages/Basket/useBasket';
import OfferDetail from '@pages/OfferDetail';
import { PDFExport } from '@progress/kendo-react-pdf';
import { filesService } from '@services/requests/filesService';
import { FileData } from '@services/requests/filesService/interface';
import { orderService } from '@services/requests/orderService';
import { Order } from '@services/requests/orderService/interface';
import { currencyLoader } from '@services/requests/searchLoaders/currencyLoader';
import { CurrencyData } from '@services/requests/searchLoaders/currencyLoader/CurrencyLoaderQuery';
import clsx from 'clsx';
import i18n from 'i18next';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import getTaxNames, { TaxNameProps } from '../../helpers/getTaxNames';
import Container from './StyledComponents';

const loadPreOrder = async (id: string) => {
  return await orderService('').Load('preOrder', id);
};

async function waitForStableRender() {
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch (e) {}
  }

  await new Promise(requestAnimationFrame);
  await new Promise(requestAnimationFrame);

  await new Promise((res) => setTimeout(res, 100));

  return new Promise((res) => setTimeout(res, 20));
}

const PreOrderPage = () => {
  const { useBaseData } = useBaseDataContext();
  const [order, setOrder] = useState<Order | undefined>(undefined);
  const pdfExportComponent = React.useRef(null);
  const button = React.useRef(null);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [files, setFiles] = useState<FileData[]>([]);
  const [taxNames, setTaxNames] = useState<TaxNameProps[]>([]);
  const [baseData] = useBaseData().useState();
  const langCode = getLocalizationById(order?.language_id)?.countryCode;
  const { useOrder } = useBasket();
  const setBasketOrder = useOrder().setState;
  const { t } = useTranslation('OfferContainers', { lng: langCode });
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyData[]>([]);
  const [customsPaymentsMode, setCustomsPaymentsMode] = useState<CustomsPaymentsMode>('full');

  useEffect(() => {
    if (!langCode) {
      return;
    }
    i18n.changeLanguage(langCode);
  }, [langCode]);

  const exportPDFWithComponent = () => {
    if (pdfExportComponent.current) {
      pdfExportComponent.current.save();
      setIsSaved(true);
    }
  };

  useLayoutEffect(() => {
    if (!baseData) {
      return;
    }
    const {
      id = '',
      needPrint = '0',
      selectedProduct,
      customsPaymentsMode,
    } = parseUrl(window.location.href, window.location.search);

    setSelectedProducts(selectedProduct);
    setCustomsPaymentsMode(customsPaymentsMode);

    currencyLoader().Load().then(setCurrencies);
    loadPreOrder(id).then(async (order) => {
      const filesToLoad = getFilesIds(order.products, baseData);
      await filesService().LoadFilesById(filesToLoad).then(setFiles);
      await getTaxNames(order?.language_id).then(setTaxNames);
      setOrder(order);
      setBasketOrder({ ...order, isLoading: false, editProduct: null });

      if (needPrint !== '1') {
        return;
      }

      requestAnimationFrame(async () => {
        requestAnimationFrame(async () => {
          await waitForStableRender();
          if (!isSaved && !!order) {
            exportPDFWithComponent();
          }
        });
      });
    });
  }, [baseData]);

  if (!baseData || order === undefined) {
    return (
      <Container>
        <LinearProgress />
      </Container>
    );
  }

  const productsToDisplay =
    selectedProducts.length > 0
      ? selectedProducts
      : [...order.products, ...(order?.pre_order_products ?? [])].map((p) => p.id);

  return (
    <PDFExport
      ref={pdfExportComponent}
      paperSize="A4"
      scale={0.4}
      keepTogether={'.kendo-pdf--prevent-split'}
      fileName={`Report for ${new Date().getFullYear()}`}
    >
      <Container className={clsx('is-pdf-export', langCode)}>
        <Button style={{ display: 'none' }} ref={button} onClick={exportPDFWithComponent}>
          Print
        </Button>
        <OfferDetail
          customsPaymentsMode={customsPaymentsMode}
          isUseActualCourses={true}
          isExportMode={true}
          files={files}
          taxNames={taxNames}
          order={order}
          baseData={{ ...baseData, currencies }}
          selectedProducts={productsToDisplay}
          title={t('OfferContainers:OfferDetailTitle')}
        />
      </Container>
    </PDFExport>
  );
};

export default PreOrderPage;
